/* eslint-disable react-hooks/rules-of-hooks */
import {
  BaseBoxShapeUtil,
  Box2d,
  Geometry2d,
  HTMLContainer,
  Matrix2d,
  Rectangle2d,
  SVGContainer,
  T,
  TLAssetId,
  TLImageAsset,
  TLOnDoubleClickHandler,
  TLOnResizeHandler,
  TLOnTranslateHandler,
  Vec2d,
  assetIdValidator,
  resizeBox,
  rotateSelectionHandle,
  toDomPrecision,
} from '@tldraw/tldraw'
import { MaskShape, MaskShapeProps } from './Types'
import { CSSProperties, useId } from 'react'
import { SVGCommand, parsePath, roundCommands } from 'svg-round-corners'
import {
  CornerRoundingStyle,
  StrokeColorStyle,
  StrokeWidthStyle,
} from 'components/VisualStudioPlayground/VisualEditor/StyleProps'
import { getContainerStyle, getContentBounds } from './helpers'
import { getDataURIFromURL, noCacheUrl } from '../ImageShape/helpers'
import { fitAndPosition } from 'object-fit-math'
import { MASK_PLACEHOLDER_IMG_SRC } from './constants'
import { useIsMasking } from 'components/VisualStudioPlayground/VisualEditor/hooks/useIsMasking'
import { CustomEqualitySet } from 'components/VisualStudioPlayground/VisualEditor/utils/customEqualitySet'
import { isEqual } from 'lodash'
import { SolidColor } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { maskShapeMigrations } from './MaskShape'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ReplaceableOverlay } from '../ImageShape/ReplaceableOverlay'

export class MaskShapeUtil extends BaseBoxShapeUtil<MaskShape> {
  static type = 'mask'

  static override migrations = maskShapeMigrations

  static override props = {
    svgDefs: T.string,
    w: T.positiveNumber,
    h: T.positiveNumber,
    mask: T.object({
      topLeft: T.object({
        x: T.number,
        y: T.number,
        z: T.number.optional(),
      }),
      bottomRight: T.object({
        x: T.number,
        y: T.number,
        z: T.number.optional(),
      }),
    }),
    assetId: assetIdValidator.nullable(),
    contentRotation: T.number,
    cornerRounding: CornerRoundingStyle,
    strokeWidth: StrokeWidthStyle,
    strokeColor: StrokeColorStyle,
    isReplaceable: T.boolean,
    wasReplaced: T.boolean,
    isEditable: T.boolean,
    isAutoUpdatable: T.boolean,
  }

  shouldDeselectAfterTranslating = false

  getDefaultProps(): MaskShapeProps {
    return {
      svgDefs: '',
      w: 100,
      h: 100,
      mask: {
        topLeft: { x: 0, y: 0 },
        bottomRight: { x: 0, y: 0 },
      },
      assetId: null,
      contentRotation: 0,
      cornerRounding: 0,
      strokeWidth: 0,
      strokeColor: '#000000',
      isReplaceable: false,
      wasReplaced: false,
      isEditable: true,
      isAutoUpdatable: true,
    }
  }

  getGeometry(shape: MaskShape): Geometry2d {
    return new Rectangle2d({
      width: shape.props.w,
      height: shape.props.h,
      isFilled: true,
    })
  }

  getCornerRoundedPath = (shape: MaskShape) => {
    const { w, h, cornerRounding } = shape.props
    const doc = new DOMParser().parseFromString(shape.props.svgDefs, 'image/svg+xml')
    const svg = doc.querySelector('svg') as SVGElement
    const originalSvgWidth = parseFloat(svg.getAttribute('width') || '0')
    const originalSvgHeight = parseFloat(svg.getAttribute('height') || '0')
    const children = Array.from(doc.querySelector('clipPath')?.children || [])
    const child = children[0]
    const commands = parsePath(child.getAttribute('d') || '')
    const scaledCommands = commands.map((command) => {
      if (!command.values) return command
      const values: SVGCommand['values'] = {}
      if ('x' in command.values) values.x = (command.values.x / originalSvgWidth) * w
      if ('y' in command.values) values.y = (command.values.y / originalSvgHeight) * h
      if ('x1' in command.values) values.x1 = (command.values.x1 / originalSvgWidth) * w
      if ('y1' in command.values) values.y1 = (command.values.y1 / originalSvgHeight) * h
      if ('x2' in command.values) values.x2 = (command.values.x2 / originalSvgWidth) * w
      if ('y2' in command.values) values.y2 = (command.values.y2 / originalSvgHeight) * h
      return { ...command, values }
    })
    const scaledRoundedCommands = roundCommands(scaledCommands, cornerRounding)
    const path = scaledRoundedCommands.path
    return path
  }

  component(shape: MaskShape) {
    const containerStyle = getContainerStyle(shape)
    const {
      id,
      props: { w, h, strokeWidth, strokeColor, assetId },
    } = shape

    const { isMobile } = useResponsiveDesign()

    const asset = assetId ? this.editor.getAsset(assetId) : undefined
    const isMasking = useIsMasking(id)
    const isSelected = shape.id === this.editor.getOnlySelectedShape()?.id
    const showMaskPreview =
      isSelected && isMasking && this.editor.isInAny('select.mask', 'select.masking', 'select.pointing_mask_handle')
    const imgSrc = asset?.props.src || MASK_PLACEHOLDER_IMG_SRC

    let path = ''
    const pathId = useId()
    const doc = new DOMParser().parseFromString(shape.props.svgDefs, 'image/svg+xml')
    doc.querySelector('clipPath')?.setAttribute('id', pathId)
    const svg = doc.querySelector('svg') as SVGElement
    svg.setAttribute('width', w.toString())
    svg.setAttribute('height', h.toString())
    const child = Array.from(doc.querySelector('clipPath')?.children || [])[0]
    path = this.getCornerRoundedPath(shape)
    child.setAttribute('d', path)

    const svgDefs = new XMLSerializer().serializeToString(doc)

    const img = (style: CSSProperties = {}) => (
      <img
        src={imgSrc}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'fill',
          position: 'absolute',
          ...style,
        }}
        draggable={false}
        alt=""
      />
    )

    return (
      <>
        {showMaskPreview && <div style={{ ...containerStyle, position: 'relative' }}>{img({ opacity: 0.4 })}</div>}
        <HTMLContainer
          id={id}
          style={{
            overflow: 'hidden',
            width: w,
            height: h,
            clipPath: `url(#${pathId})`,
            pointerEvents: 'all',
          }}
        >
          <div
            style={{ ...containerStyle, position: 'relative' }}
            onPointerDown={() => {
              if (isMobile) {
                const shapeUtil = this.editor.getShapeUtil('mask') as MaskShapeUtil
                shapeUtil.shouldDeselectAfterTranslating = this.editor.getSelectedShapes().length === 0
              }
            }}
          >
            {img()}
          </div>
          {this.canBeReplaced(shape) && <ReplaceableOverlay shapeId={shape.id} />}
        </HTMLContainer>
        <div dangerouslySetInnerHTML={{ __html: svgDefs }} />
        {shape.props.strokeWidth > 0 && (
          <SVGContainer style={{ width: w, height: h }}>
            <path d={path} stroke={strokeColor} strokeWidth={strokeWidth} fill="none" />
          </SVGContainer>
        )}
      </>
    )
  }

  indicator(shape: MaskShape) {
    if (useIsMasking(shape.id)) return null

    const bounds = this.editor.getShapeGeometry(shape).bounds
    return <rect width={toDomPrecision(bounds.width)} height={toDomPrecision(bounds.height)} />
  }

  onDoubleClick: TLOnDoubleClickHandler<MaskShape> | undefined = (shape) => {
    if (this.editor.isIn('select.mask.idle')) {
      this.editor.setCurrentTool('select')
    } else {
      this.editor.setCurrentTool('select.mask.idle')
    }
    return shape
  }

  isColorisable = () => false

  override onResize: TLOnResizeHandler<MaskShape> = (shape, info) => {
    if (!shape.props.mask) return
    if (['top_left', 'top_right', 'bottom_left', 'bottom_right'].includes(info.handle)) {
      const parent = this.editor.getShapeParent(shape.id)
      const parentPosition = parent ? new Vec2d(parent.x, parent.y) : new Vec2d(0, 0)
      const maskBoundingBox = new Box2d(shape.x, shape.y, shape.props.w, shape.props.h)
      const rotation = shape.rotation
      const scaleOriginHandle = rotateSelectionHandle(info.handle, Math.PI)
      const scaleOriginPage = Vec2d.RotWith(
        maskBoundingBox.getHandlePoint(scaleOriginHandle),
        maskBoundingBox.point,
        rotation,
      ).add(parentPosition)
      const distanceFromScaleOriginNow = Vec2d.Sub(this.editor.inputs.currentPagePoint, scaleOriginPage).rot(-rotation)
      const distanceFromScaleOriginAtStart = Vec2d.Sub(this.editor.inputs.originPagePoint, scaleOriginPage).rot(
        -rotation,
      )

      let scale = Vec2d.DivV(distanceFromScaleOriginNow, distanceFromScaleOriginAtStart)

      if (Math.abs(scale.x) > Math.abs(scale.y)) {
        scale.y = Math.abs(scale.x) * (scale.y < 0 ? -1 : 1)
      } else {
        scale.x = Math.abs(scale.y) * (scale.x < 0 ? -1 : 1)
      }

      scale = Vec2d.Clamp(scale, 0)

      const pageTransform = Matrix2d.Identity().translate(shape.x, shape.y).rotate(shape.rotation)

      // @ts-ignore
      const newPagePoint = this.editor._scalePagePoint(
        Matrix2d.applyToPoint(pageTransform, parentPosition),
        scaleOriginPage,
        scale,
        rotation,
      )

      const newLocalPoint = this.editor.getPointInParentSpace(shape.id, newPagePoint)

      const newShape = {
        x: newLocalPoint.x,
        y: newLocalPoint.y,
        props: {
          w: shape.props.w * scale.x,
          h: shape.props.h * scale.y,
        },
      }

      return newShape
    }

    const result = resizeBox(shape, info)

    // Delta in pixels
    const dx = result.props.w - shape.props.w
    const dy = result.props.h - shape.props.h
    const { w, h } = getContentBounds(shape)

    const newMask = {
      topLeft: {
        ...shape.props.mask.topLeft,
      },
      bottomRight: {
        ...shape.props.mask.bottomRight,
      },
    }

    if (dx < 0) {
      // Mask is getting smaller horizontally
      const movement = Math.abs(dx) / w / 2
      newMask.topLeft.x = Math.max(newMask.topLeft.x + movement, 0)
      newMask.bottomRight.x = Math.min(1, newMask.bottomRight.x - movement)
    }

    if (dx > 0) {
      const deltaPerc = Math.abs(dx) / w
      const newTopLeftX = Math.max(newMask.topLeft.x - deltaPerc / 2, 0)
      const newBottomRightX = Math.min(newMask.bottomRight.x + deltaPerc / 2, 1)

      if (newTopLeftX === 0) {
        // Mask is at the left edge
        // Move the mask to the right
        newMask.topLeft.x = 0
        newMask.bottomRight.x = Math.min(newMask.bottomRight.x + deltaPerc - shape.props.mask.topLeft.x, 1)
      } else if (newBottomRightX === 1) {
        // Mask is at the right edge
        // Move the mask to the left
        newMask.bottomRight.x = 1
        newMask.topLeft.x = Math.max(newMask.topLeft.x - deltaPerc - shape.props.mask.bottomRight.x, 0)
      } else {
        newMask.topLeft.x = newTopLeftX
        newMask.bottomRight.x = newBottomRightX
      }
    }

    if (dy < 0) {
      // Mask is getting smaller vertically
      const movement = Math.abs(dy) / h / 2
      newMask.topLeft.y = Math.max(newMask.topLeft.y + movement, 0)
      newMask.bottomRight.y = Math.min(1, newMask.bottomRight.y - movement)
    }

    if (dy > 0) {
      // Mask is getting bigger vertically
      const deltaPerc = Math.abs(dy) / h
      const newTopLeftY = Math.max(newMask.topLeft.y - deltaPerc / 2, 0)
      const newBottomRightY = Math.min(newMask.bottomRight.y + deltaPerc / 2, 1)

      if (newTopLeftY === 0) {
        // Mask is at the top edge
        // Move the mask to the bottom
        newMask.topLeft.y = 0
        newMask.bottomRight.y = Math.min(newMask.bottomRight.y + deltaPerc - shape.props.mask.topLeft.y, 1)
      } else if (newBottomRightY === 1) {
        // Mask is at the bottom edge
        // Move the mask to the top
        newMask.bottomRight.y = 1
        newMask.topLeft.y = Math.max(newMask.topLeft.y - deltaPerc - shape.props.mask.bottomRight.y, 0)
      } else {
        newMask.topLeft.y = newTopLeftY
        newMask.bottomRight.y = newBottomRightY
      }
    }

    if (newMask.topLeft.x === 0 && newMask.bottomRight.x === 1) {
      const maskHeightPerc = newMask.bottomRight.y - newMask.topLeft.y
      const maskHeightPx = maskHeightPerc * h
      const newImageHeight = (result.props.w * h) / w
      const newMaskHeightPerc = maskHeightPx / newImageHeight
      newMask.bottomRight.y = newMask.topLeft.y + newMaskHeightPerc
    }

    if (newMask.topLeft.y === 0 && newMask.bottomRight.y === 1) {
      const maskWidthPerc = newMask.bottomRight.x - newMask.topLeft.x
      const maskWidthPx = maskWidthPerc * w
      const newImageWidth = (result.props.h * w) / h
      const newMaskWidthPerc = maskWidthPx / newImageWidth
      newMask.bottomRight.x = newMask.topLeft.x + newMaskWidthPerc
    }

    const newShape = {
      ...result,
      props: {
        ...result.props,
        mask: newMask,
      },
    }

    return newShape
  }

  onBeforeUpdate = (prev: MaskShape, next: MaskShape) => {
    if (!prev.props.isEditable) {
      return { ...prev, props: { ...prev.props, isEditable: next.props.isEditable } }
    }

    return next
  }

  onTranslate: TLOnTranslateHandler<MaskShape> = (initialShape) => {
    if (!initialShape.props.isEditable) {
      this.editor.snaps.clear()
    }
  }

  onTranslateEnd = () => {
    if (this.shouldDeselectAfterTranslating) {
      this.editor.selectNone()
    }
    this.shouldDeselectAfterTranslating = false
  }

  getColors = (shape: MaskShape) => {
    const colors = new CustomEqualitySet<SolidColor>(isEqual)

    if (shape.props.strokeWidth > 0) {
      colors.add({ type: 'solidColor', color: shape.props.strokeColor, opacity: 1 })
    }

    return colors
  }

  /**
   * Returns true if the shape can be replaced and has not been replaced yet
   * @param shape The shape to check
   */
  canBeReplaced = (shape: MaskShape) => shape.props.isReplaceable && !shape.props.wasReplaced

  /**
   * If the image is replaceable, replaces the image with a new one with the given assetId,
   * otherwise does nothing. The new image will maintain the same dimensions and position as the old one.
   */
  replace = async (shape: MaskShape, assetId: TLAssetId) => {
    const asset = this.editor.getAsset(assetId) as TLImageAsset | undefined

    if (!asset || asset.props.mimeType === 'image/svg+xml') return

    const imgFit = fitAndPosition(
      { width: shape.props.w, height: shape.props.h },
      { width: asset.props.w, height: asset.props.h },
      'cover',
      '50%',
      '50%',
    )
    this.editor.batch(() => {
      this.editor.updateShape({
        ...shape,
        props: {
          wasReplaced: true,
          isReplaceable: shape.props.isReplaceable,
          assetId: asset.id,
          mask: {
            topLeft: {
              x: Math.abs(imgFit.x / imgFit.width),
              y: Math.abs(imgFit.y / imgFit.height),
            },
            bottomRight: {
              x: (imgFit.x + imgFit.width) / imgFit.width,
              y: (imgFit.y + imgFit.height) / imgFit.height,
            },
          },
        },
      })
      this.editor.select(shape.id)
      this.editor.setHintingShapes([])
    })
  }

  /**
   * Updates the isReplaceable property of the shape and sets the wasReplaced property to false
   * @param shape The shape to update
   * @param isReplaceable The new value of isReplaceable
   * @returns void
   */
  setReplaceable = (shape: MaskShape, isReplaceable: boolean) => {
    this.editor.updateShapes([
      {
        ...shape,
        props: {
          isReplaceable,
          wasReplaced: false,
        },
      },
    ])
  }

  override async toSvg(shape: MaskShape) {
    const containerStyle = getContainerStyle(shape)

    const { transform, width, height } = containerStyle

    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')

    const clipPathPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    clipPathPath.setAttribute('d', this.getCornerRoundedPath(shape))

    const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath')
    clipPath.setAttribute('id', `cropClipPath-${shape.id}`)
    clipPath.appendChild(clipPathPath)

    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    defs.appendChild(clipPath)
    g.appendChild(defs)

    const src = await this.getSrcAsDataURI(shape)
    const image = document.createElementNS('http://www.w3.org/2000/svg', 'image')
    image.setAttributeNS('http://www.w3.org/1999/xlink', 'href', src)
    image.setAttribute('width', width.toString())
    image.setAttribute('height', height.toString())
    image.style.transform = transform

    const innerElement = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    innerElement.setAttribute('clip-path', `url(#cropClipPath-${shape.id})`)

    innerElement.appendChild(image)

    g.appendChild(innerElement)

    if (shape.props.strokeWidth > 0) {
      const border = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      border.setAttribute('id', `border-${shape.id}`)
      border.setAttribute('d', this.getCornerRoundedPath(shape))
      border.setAttribute('width', width.toString())
      border.setAttribute('height', height.toString())
      border.setAttribute('fill', 'none')
      border.setAttribute('stroke', shape.props.strokeColor)
      border.setAttribute('stroke-width', shape.props.strokeWidth.toString())
      g.appendChild(border)
    }

    return g
  }

  private async getSrcAsDataURI(shape: MaskShape) {
    const asset = shape.props.assetId ? this.editor.getAsset(shape.props.assetId) : null
    const src = asset?.props.src || ''

    if (src && src.startsWith('http')) {
      return (await getDataURIFromURL(noCacheUrl(src))) || ''
    }

    return ''
  }

  hideRotateHandle = () => true
}
